/**
 * useOfflineQueue - Thin React wrapper over OfflineQueueService
 * 
 * This hook is just a bridge between React and the service.
 * All business logic lives in offlineQueueService.ts
 */

import { useState, useEffect, useCallback } from 'react';
import { offlineQueueService, type QueuedExpense } from '@/services/offlineQueueService';

export const useOfflineQueue = () => {
  const [queue, setQueue] = useState<QueuedExpense[]>([]);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isSyncing, setIsSyncing] = useState(false);

  useEffect(() => {
    // Initialize service
    offlineQueueService.initialize().then(() => {
      setQueue(offlineQueueService.getQueue());
      setIsSyncing(offlineQueueService.getIsSyncing());
    });

    // Subscribe to queue changes
    const unsubscribe = offlineQueueService.subscribe((newQueue) => {
      setQueue(newQueue);
      setIsSyncing(offlineQueueService.getIsSyncing());
    });

    // Listen for online/offline events
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      unsubscribe();
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    // Auto-sync when coming online
    if (isOnline && queue.length > 0 && !isSyncing) {
      offlineQueueService.syncQueue();
    }
  }, [isOnline, queue.length, isSyncing]);

  const enqueue = useCallback((action: 'insert' | 'update' | 'delete', data: any) => {
    offlineQueueService.enqueue(action, data);
  }, []);

  const syncQueue = useCallback(async () => {
    await offlineQueueService.syncQueue();
  }, []);

  return {
    queue,
    enqueue,
    syncQueue,
    isOnline,
    isSyncing,
    pendingCount: queue.length,
  };
};
